import { t } from '@bia-auction/shared-i18n';
import {
  EXPERTISE_PARTS,
  EXPERTISE_STATES,
  type ExpertisePart,
  type ExpertiseReport,
  type ExpertiseState,
  isOriginalVehicle,
} from '@bia-auction/shared-schema';
import { Select, StatusBadge } from '@bia-auction/ui';
import { CheckCircle2, Info } from 'lucide-react';

/**
 * The 14-part paint/replacement report (redesign-vehicle.md §3, OQ-1 resolved: labelled grid
 * first, the legacy SVG car diagram is a possible follow-up).
 *
 * The load-bearing rule: **an unassessed panel is ABSENT, not `original`**. Defaulting a
 * panel nobody looked at to "original" would put a claim about the car in front of bidders
 * that we cannot back — so the empty option writes no key at all, and the derived "Orijinal"
 * badge (shown live here) needs all 14 panels present and original.
 */
export function ExpertiseEditor({
  value,
  onChange,
  disabled,
}: {
  value: ExpertiseReport;
  onChange: (next: ExpertiseReport) => void;
  disabled?: boolean;
}) {
  const setPart = (part: ExpertisePart, state: string) => {
    const next: ExpertiseReport = { ...value };
    if (state === '') {
      // Back to "not assessed" — delete the key rather than storing an empty string, so the
      // payload keeps meaning "nobody looked at this".
      delete next[part];
    } else {
      next[part] = state as ExpertiseState;
    }
    onChange(next);
  };

  const assessed = EXPERTISE_PARTS.filter((p) => value[p]).length;
  const clean = isOriginalVehicle(value);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {clean ? (
          <StatusBadge variant="success">
            <CheckCircle2 aria-hidden className="h-3.5 w-3.5" />
            {t('expertise.originalBadge')}
          </StatusBadge>
        ) : (
          <span className="text-sm text-muted-foreground tabular-nums">
            {assessed} / {EXPERTISE_PARTS.length}
          </span>
        )}
        <p className="flex items-start gap-1.5 text-sm text-muted-foreground">
          <Info aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          {t('expertise.notAssessedHint')}
        </p>
      </div>

      <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        {EXPERTISE_PARTS.map((part) => {
          const id = `expertise-${part}`;
          return (
            <div key={part} className="space-y-1.5">
              <label htmlFor={id} className="text-sm font-medium">
                {t(`expertise.part.${part}`)}
              </label>
              <Select
                id={id}
                value={value[part] ?? ''}
                disabled={disabled}
                onChange={(e) => setPart(part, e.target.value)}
              >
                <option value="">{t('expertise.notAssessed')}</option>
                {EXPERTISE_STATES.map((state) => (
                  <option key={state} value={state}>
                    {t(`expertise.state.${state}`)}
                  </option>
                ))}
              </Select>
            </div>
          );
        })}
      </div>
    </div>
  );
}
